import React, {useEffect, useState} from "react";
import Tr from "translations/Translations";
import PropTypes from "prop-types";
import OfferChoiceService from "services/OfferChoiceService";
import Dialog from "components/Dialog";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import Loading from "components/Loading";
import GridBlock from "components/GridBlock";
import BoldTypography from "components/BoldTypography";
import {makeStyles} from "@material-ui/core";
import Form from "components/Form";
import useId from "hooks/useId";
import * as Yup from "yup";
import VS from "constants/ValidationShema";
import DateTimePicker from "components/DateTimePicker";
import TypographyWithIcon from "components/TypographyWithIcon";
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {connect} from "react-redux";
import {RoundType} from "constants/RoundType";
import CreateAdditionalRoundTable from "./CreateAdditionalRoundTable";

const useStyle = makeStyles(theme => ({
    cells: {
        padding: theme.spacing()
    },
    partColumn: {
        width: 75
    },
    dateTimePicker: {
        width: 350
    }
}));


const OfferChoiceCreateAdditionalRoundPopup = (
    {
        demandId,
        onCancel,
        onSuccess,
        roundType,
        formSubmit
    }) => {

    const isAuction = roundType === RoundType.AUCTION;
    const classes = useStyle();

    const [rounds, setRounds] = useState(null);
    const [showContractors, setShowContractors] = useState(false);
    const [working, setWorking] = useState(true);
    const [error, setError] = useState(false);
    const formId = useId();
    const handleSubmit = () => formSubmit[formId]();

    const onSubmit = (values) => {
        setError(false);
        setWorking(true);
        OfferChoiceService.createAdditionalRound(demandId, values, roundType).then(onSuccess).catch(() => {
            setError(true);
            setWorking(false);
        })
    }

    useEffect(() => {
        OfferChoiceService.prepareAdditionalRound(demandId, roundType).then(resp => {
            setRounds({...resp, positions: resp.positions.map(x => ({...x, identity: "_" + x.identity, contractors: !!x.contractors ? x.contractors.map(c => ({...c, identity: "_" + c.identity})) : []}))})
            setShowContractors(resp.showContractors);
        }).finally(() => setWorking(false));
    }, [demandId, roundType])

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onCancel, 'outlined', working));
        buttons.push(createButton(Tr(isAuction ? 'additional_round_create_auction_begin_button' : 'additional_round_create_additional_offers_begin_button'), handleSubmit, undefined, working || !rounds?.positions.length));
        return buttons;
    }
    const handleEndDate = (formikProps, value) => {
        formikProps.setFieldValue('openOfferDate', value);
    }

    const validationSchema = () => {
        const shape = {
            parts: VS.any().test('partSelected', Tr('choose_at_least_one_position'), (e) => {
                const selIndex = Object.keys(e).findIndex(x => e[x].checked === true);
                return selIndex > -1;
            }).test('contractorSelected', Tr('choose_at_least_one_contractor'), (e) => {
                return showContractors ? Object.keys(e).filter(x => e[x].checked === true).every(x => Object.keys(e[x].contractors).findIndex(c => e[x].contractors[c] === true) > -1) : true;
            })
        }
        if (!isAuction) {
            shape.endDate = VS.dateTimeMin(new Date(), Tr('v_time_can_not_be_past'));
            shape.openOfferDate = VS.dateTimeValid()
                .when('endDate', (endDate, schema) => {
                    return schema.test('EndDateLessOpenDate', Tr("EndDateLessOpenDate"), (offerOpenDate) => {
                        if (endDate && offerOpenDate) {
                            return !offerOpenDate.isBefore(endDate);
                        }
                        return false;
                    })
                })
        }
        return Yup.object().shape(shape)
    }

    return (
        <Dialog
            open
            dialogActions={<MultiButton buttons={getButtons()}/>}
            title={Tr(isAuction ? 'additional_round_create_auction_popup_title' : 'additional_round_create_additional_offers_popup_title')}
            maxWidth='md'
        >
            <GridBlock
                column
            >

                {working && <Loading/>}
                {error && <TypographyWithIcon Icon={ErrorOutlineIcon} color='error'>{Tr('unknown_error_message')}</TypographyWithIcon>}
                {!working && !!rounds && <>

                    <Form
                        initialValues={{
                            endDate: null,
                            openOfferDate: null,
                            parts: rounds.positions.reduce((prev, cur) => {
                                prev[cur.identity] = {
                                    checked: false,
                                    contractors: cur.contractors.reduce((arr, elem) => {
                                        arr[elem.identity] = false;
                                        return arr;
                                    }, {})
                                };
                                return prev;
                            }, {})
                        }}
                        validationSchema={validationSchema()}
                        onSubmit={onSubmit}
                        id={formId}
                    >
                        {(formikProps) => (
                            <GridBlock
                                column
                            >
                                {!isAuction &&
                                <DateTimePicker
                                    className={classes.dateTimePicker}
                                    id='endDate'
                                    label={Tr('additional_round_end_date')}
                                    formikProps={formikProps}
                                    disabled={working || !rounds?.positions.length}
                                    onChange={(value) => handleEndDate(formikProps, value)}
                                    minDate={new Date()}
                                />
                                }

                                {!isAuction &&
                                <DateTimePicker
                                    className={classes.dateTimePicker}
                                    id='openOfferDate'
                                    label={Tr('additional_round_open_offer_date')}
                                    formikProps={formikProps}
                                    disabled={working || !rounds?.positions.length}
                                    minDate={new Date()}
                                />
                                }
                                {
                                    !(rounds?.positions.length) ?
                                        <BoldTypography variant='body1'>
                                            {
                                                isAuction ? (rounds.error ? rounds.error : Tr('additional_round_auction_list_empty')) : Tr('additional_round_list_empty')
                                            }
                                        </BoldTypography>
                                        :
                                        <CreateAdditionalRoundTable
                                            label={Tr(isAuction ? 'additional_round_auction_list_title' : 'additional_round_list_title')}
                                            id={'parts'}
                                            formikProps={formikProps}
                                            elementList={rounds.positions}
                                            showContractors={showContractors}
                                        />
                                }
                            </GridBlock>
                        )}
                    </Form>


                </>}
            </GridBlock>


        </Dialog>)
}

OfferChoiceCreateAdditionalRoundPopup.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    roundType: PropTypes.oneOf([RoundType.AUCTION, RoundType.ADDITIONAL_OFFERS]).isRequired
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(OfferChoiceCreateAdditionalRoundPopup)